import { cur } from '@/lib/currency';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, ArrowRight } from 'lucide-react';
import { localToday } from '@/lib/dateRange';
import { useSettleAll } from '@/hooks/useSettleAll';

const fmtSR = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => localToday();

/**
 * SettleAllButton — iter402 (rewritten iter859 to use the shared
 * useSettleAll hook instead of duplicating the api.post/toast logic).
 *
 * One-click bulk settle for every netted inter-branch pair in the
 * current period.
 */
export function SettleAllButton({ netted = [], dateFilter, onDone }) {
  const [open, setOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [date, setDate] = useState(todayISO());

  const positivePairs = netted.filter((n) => Number(n?.owes || 0) > 0);
  const totalAmount = positivePairs.reduce((s, n) => s + Number(n.owes || 0), 0);

  const { settle, posting } = useSettleAll({
    label: 'pair',
    fmtAmount: fmtSR,
    onDone: () => {
      setOpen(false);
      if (typeof onDone === 'function') onDone();
    },
  });

  const handleSettle = () => settle(
    {
      start_date: dateFilter?.start || null,
      end_date: dateFilter?.end || null,
      payment_mode: paymentMode,
      date,
    },
    positivePairs.length,
  );

  return (
    <>
      <Button
        size="sm"
        variant="default"
        disabled={positivePairs.length === 0}
        onClick={() => setOpen(true)}
        className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
        data-testid="settle-all-btn"
        title={positivePairs.length === 0 ? 'No outstanding inter-branch balances in this period' : `Bulk-settle ${positivePairs.length} pair(s)`}
      >
        <Wallet size={14} className="mr-1" />
        Settle All ({positivePairs.length})
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="settle-all-dialog">
          <DialogHeader>
            <DialogTitle>Settle all inter-branch balances?</DialogTitle>
            <DialogDescription>
              This will create <b>{positivePairs.length}</b> settlement{positivePairs.length === 1 ? '' : 's'} and <b>{positivePairs.length}</b> journal entr{positivePairs.length === 1 ? 'y' : 'ies'} — one per branch pair below — totalling <b>{fmtSR(totalAmount)}</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-border max-h-[240px] overflow-y-auto">
              {positivePairs.map((n, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs border-b last:border-b-0" data-testid={`settle-all-row-${i}`}>
                  <span className="font-semibold text-error">{n.from_branch}</span>
                  <ArrowRight size={11} className="text-muted-foreground shrink-0" />
                  <span className="font-semibold text-success">{n.to_branch}</span>
                  <span className="ml-auto font-bold tabular-nums">{fmtSR(n.owes)}</span>
                  <span className="text-[10px] text-muted-foreground">({n.transaction_count} tx)</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Payment mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger className="mt-1" data-testid="settle-all-payment-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="other">Other / Clearing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Settlement date</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1"
                  data-testid="settle-all-date"
                />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Tip: All pairs use the same payment mode + date. To settle individual pairs with different modes, use the per-row Settle buttons instead.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={posting}>Cancel</Button>
            <Button
              onClick={handleSettle}
              disabled={posting || positivePairs.length === 0}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="settle-all-confirm"
            >
              {posting ? 'Settling…' : `Settle ${positivePairs.length} pair${positivePairs.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SettleAllButton;
